// marketplace-helpers.ts — pure helpers used by the marketplace store
// (marketplace-store.svelte.ts) and the onboarding wizard. Kept in a plain .ts
// file (no $state, no svelte runtime) so they can be unit-tested with bun:test
// directly. Re-exported from the store; consumers should import from there.

import type { CatalogEntry, CapabilityKind, CapabilityAuth, CapabilityHealth, ShareStatus, Me } from './marketplace-api';
import { presentationForCapability } from './capability-presentation';

export type KindFilter = 'all' | 'mcp' | 'skill';

// Split a catalog into installed and available, preserving order within each
// half. The API returns name-asc; we keep that ordering.
export function splitCatalog(
	entries: CatalogEntry[],
): { installed: CatalogEntry[]; available: CatalogEntry[] } {
	const installed: CatalogEntry[] = [];
	const available: CatalogEntry[] = [];
	for (const e of entries) {
		if (e.installed) installed.push(e);
		else available.push(e);
	}
	return { installed, available };
}

// ── Fuzzy search (SAY-1689) ──────────────────────────────────────────────────
// Lower-case, and fold `_` / `-` into spaces so "amazon_ads", "amazon-ads" and
// "amazon ads" are the same thing on both the query and the haystack side.
function normalizeSearchText(s: string): string {
	return s
		.toLowerCase()
		.replace(/[_\-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

// Damerau-free Levenshtein distance, early-exited at `max + 1`.
function editDistance(a: string, b: string, max: number): number {
	if (Math.abs(a.length - b.length) > max) return max + 1;
	let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		const cur = [i];
		let rowMin = i;
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
			cur.push(v);
			if (v < rowMin) rowMin = v;
		}
		if (rowMin > max) return max + 1;
		prev = cur;
	}
	return prev[b.length];
}

// One query word matches the haystack when it is a substring, or when some
// haystack word is within a small edit distance of it. Short words (≤3 letters)
// get no typo budget — otherwise "ask" would light up half the catalog.
function wordMatches(qw: string, hay: string, hayWords: string[]): boolean {
	if (hay.includes(qw)) return true;
	const budget = qw.length >= 7 ? 2 : qw.length >= 4 ? 1 : 0;
	if (budget === 0) return false;
	return hayWords.some((hw) => editDistance(qw, hw, budget) <= budget);
}

// Fuzzy, order-insensitive, word-wise match: every query word must match
// somewhere in the joined fields. Empty query matches everything.
export function matchesSearch(query: string, ...fields: string[]): boolean {
	const q = normalizeSearchText(query);
	if (!q) return true;
	const hay = normalizeSearchText(fields.join(' '));
	const hayWords = hay.split(' ');
	return q.split(' ').every((qw) => wordMatches(qw, hay, hayWords));
}

// Fuzzy word-wise + kind filter. The search matches name + summary
// case-insensitively (description is too noisy; publisher/id are technical
// fields hidden in the non-technical-friendly rework — roadmap item #3).
export function filterCatalog(
	entries: CatalogEntry[],
	query: string,
	kind: KindFilter,
): CatalogEntry[] {
	return entries.filter((e) => {
		if (kind !== 'all' && e.kind !== kind) return false;
		return matchesSearch(query, e.name, e.summary);
	});
}

// A locked entry is one the user can SEE but not install — the admin-tier
// tools shown as greyed cards to non-admins. The backend forces
// `installed:false` for these and suppresses the health/connected fields, so
// the frontend gate is the visibility flag alone.
// A "locked" entry is one the user can SEE in /catalog but should NOT render as
// an actionable card (buildCards drops it). With migration 005 the server computes
// the effective `installable` per user (active + visibility + grants), so the lock
// is driven off THAT rather than a client re-derivation of visibility — this is
// what makes a globally-deactivated or un-granted cap disappear for members.
// EXCEPTION: an INSTALLED cap that just became un-installable (an admin turned it
// off / revoked access) is NOT locked — it stays visible so the card can show an
// honest "Turned off by your admin" state instead of silently vanishing.
export function isLocked(entry: CatalogEntry, me: Me | null): boolean {
	if (!me) return false;
	if (entry.tier === 'personal') return false; // owner-gated; never a locked card
	return !entry.installable && !entry.installed;
}

// ── Pair-merge data model (A1) ────────────────────────────────────────────────
// The curated fleet ships read/write TWINS: for each base id `x` there's a
// write entry `x` and a read entry `x-readonly`. The redesign collapses each
// surviving pair into ONE Card so non-technical users see "Asana" once, with a
// quiet "let it make changes" switch, instead of two near-identical cards.

// Strict suffix pairing. `x` ⇄ `x-readonly` ONLY — NOT brand-based, so
// amazon-ads and amazon-seller never merge (they'd share a brand but not a base).
// Pure string math; does not consult the catalog.
export function twinOf(id: string): { base: string; readId: string; writeId: string } {
	const base = id.endsWith('-readonly') ? id.slice(0, -'-readonly'.length) : id;
	return { base, readId: `${base}-readonly`, writeId: base };
}

// One unified card the redesigned page + onboarding render. A merged twin pair
// or a singleton both flatten to this shape.
export type Card = {
	key: string; // the base id (stable react/svelte key + grouping anchor)
	kind: CapabilityKind;
	name: string; // brand label (presentation.label) with twin parentheticals stripped
	brand: string | undefined; // presentation brand glyph key (undefined → fallback)
	essence: string; // calm card subtitle (presentation.essence of the active twin)
	// Twin ids that SURVIVED admin-locking for this viewer. null when that twin
	// doesn't exist or is admin-locked away. For a singleton both are null.
	readId: string | null;
	writeId: string | null;
	primaryId: string; // the id On/Off + connect act on (the active twin)
	installed: boolean;
	enabled: boolean;
	requiresConnection: boolean;
	connected: boolean;
	// The REAL, broker-verified auth state of the active twin (mirrors CatalogEntry.
	// authState). Preferred over the group-aware `connected` boolean by cardStatus —
	// undefined for non-requiresConnection caps and pre-authState payloads.
	authState: 'authed' | 'needs_auth' | 'not_connected' | undefined;
	health: CapabilityHealth | undefined;
	// Admin turned this off / revoked access while the user still has it installed
	// (migration 005). Drives an honest "Turned off by your admin" card state.
	unavailableReason: 'admin_off' | undefined;
	auth: CapabilityAuth | undefined;
	access: 'read_only' | 'read_write';
	recommended: boolean;
	// UI grouping bucket (migration 014). null ⇒ the card renders flat in the
	// Skills room; a named group (e.g. 'otto') collapses its members into one
	// collapsible group block. Copied straight from the active entry.
	group: string | null;
	owner: string | null;
	shareStatus: ShareStatus;
	// The "let it make changes" affordance. visible only when BOTH twins are
	// installable for this viewer (Asana for everyone, all 6 brands for admins);
	// on when the write twin is installed + enabled.
	accessSwitch: { visible: boolean; on: boolean };
};

// Strip the seed's twin parentheticals from a display name so a fallback name
// (when no presentation.label exists) reads cleanly on a merged card.
function stripTwinSuffix(name: string): string {
	return name.replace(/\s*\((?:Read-only|Write-enabled)\)\s*$/i, '').trim();
}

// Merge the surviving catalog into unified Cards.
//   a. Drop admin-locked entries for this viewer (isLocked → exclude).
//   b. Pair `<base>` with `<base>-readonly`. A genuine twin pair is signalled by
//      a surviving `-readonly` entry: both twins survive → one merged Card; only
//      the read twin survives (write admin-dropped) → a standalone read-only
//      Card (writeId null). A cap with no `-readonly` sibling that isn't itself
//      a `-readonly` → a singleton Card (readId/writeId null).
//   c. Card fields derive from the ACTIVE twin (see activeEntry selection).
// Catalog order is preserved by base (first appearance wins).
export function buildCards(entries: CatalogEntry[], me: Me | null): Card[] {
	// (a) survivors only.
	const survivors = entries.filter((e) => !isLocked(e, me));
	const byId = new Map(survivors.map((e) => [e.id, e]));

	const cards: Card[] = [];
	const seenBases = new Set<string>();

	for (const e of survivors) {
		// AMC is one product card. Analytics is the canonical toggle for everyone;
		// admins receive the separately attested Inventory surface as a manager-owned
		// companion behind that same toggle.
		const isAmc = e.id === 'amazon-amc' || e.id === 'amazon-amc-readonly';
		if (isAmc && seenBases.has('amazon-amc-readonly')) continue;
		if (isAmc) {
			seenBases.add('amazon-amc-readonly');
			const analytics = byId.get('amazon-amc-readonly');
			// Gate-false v1 exposes only the historical admin row; preserve that
			// compatibility card until the coordinated v2 catalog is active.
			if (analytics) {
				const inventory = byId.get('amazon-amc');
				const inventoryActive = inventory?.installed === true && inventory.enabled;
				const healthRank: Record<CapabilityHealth, number> = {
					connected: 0,
					unknown: 1,
					failed: 2,
					needs_auth: 3,
				};
				const health = inventoryActive && inventory?.health &&
					(!analytics.health || healthRank[inventory.health] > healthRank[analytics.health])
					? inventory.health
					: analytics.health;
				const pres = presentationForCapability(
					analytics.id,
					analytics.name,
					analytics.icon,
					analytics.latestVersion,
				);
				cards.push({
					key: analytics.id,
					kind: analytics.kind,
					name: 'Amazon Marketing Cloud',
					brand: pres?.brand,
					essence: inventory ? 'Attribution, journeys & inventory' : 'Attribution & customer journeys',
					readId: null,
					writeId: null,
					primaryId: analytics.id,
					installed: analytics.installed,
					enabled: analytics.enabled,
					requiresConnection: analytics.requiresConnection ?? false,
					connected: analytics.connected ?? false,
					authState: analytics.authState,
					health,
					unavailableReason: inventory?.unavailableReason ?? analytics.unavailableReason,
					auth: analytics.auth,
					access: analytics.access,
					recommended: analytics.recommended,
					group: analytics.group,
					owner: analytics.owner,
					shareStatus: analytics.shareStatus,
					accessSwitch: { visible: false, on: false },
				});
				continue;
			}
		}
		const pair = twinOf(e.id);
		const base = pair.base;
		const { readId, writeId } = pair;
		if (seenBases.has(base)) continue; // already emitted this pair/singleton
		seenBases.add(base);

		const readEntry = byId.get(readId);
		const writeEntry = byId.get(writeId);

		// A genuine twin pair is signalled by a `-readonly` entry surviving. Without
		// one, the surviving write entry is a singleton (krea/erp-data/analytics/
		// jtl-erp/skills) — readId/writeId stay null. (We never treat a write entry
		// whose `-readonly` was admin-dropped as a twin: the spec keeps readId/
		// writeId for ACTUAL surviving twin pairs only.)
		const isTwin = Boolean(readEntry);
		const survivingReadId = readEntry ? readId : null;
		const survivingWriteId = writeEntry ? writeId : null;

		// activeEntry: prefer the installed write twin, then the installed read
		// twin, then (nothing installed) the read twin for safe-default display,
		// then the write twin (krea-like singletons), then `e` as a final fallback.
		const installedWrite = writeEntry?.installed ? writeEntry : undefined;
		const installedRead = readEntry?.installed ? readEntry : undefined;
		const activeEntry = installedWrite ?? installedRead ?? readEntry ?? writeEntry ?? e;

		const pres = presentationForCapability(
			activeEntry.id,
			activeEntry.name,
			activeEntry.icon,
			activeEntry.latestVersion,
		);
		const name = pres?.label ?? stripTwinSuffix(activeEntry.name);

		const cardReadId = isTwin ? survivingReadId : null;
		const cardWriteId = isTwin ? survivingWriteId : null;

		// The write switch is visible only when BOTH twins are installable for this
		// viewer (Asana for everyone; all 6 brands for admins). on when the write
		// twin is installed + enabled (self-heals the legacy double-install case).
		const accessVisible = Boolean(survivingReadId) && Boolean(survivingWriteId);
		const accessOn = Boolean(writeEntry?.installed && writeEntry?.enabled);

		cards.push({
			key: base,
			kind: activeEntry.kind,
			name,
			brand: pres?.brand,
			essence: pres?.essence ?? activeEntry.summary,
			readId: cardReadId,
			writeId: cardWriteId,
			primaryId: activeEntry.id,
			installed: activeEntry.installed,
			enabled: activeEntry.enabled,
			requiresConnection: activeEntry.requiresConnection ?? false,
			connected: activeEntry.connected ?? false,
			authState: activeEntry.authState,
			health: activeEntry.health,
			unavailableReason: activeEntry.unavailableReason,
			auth: activeEntry.auth,
			access: activeEntry.access,
			recommended: activeEntry.recommended,
			group: activeEntry.group,
			owner: activeEntry.owner,
			shareStatus: activeEntry.shareStatus,
			accessSwitch: { visible: accessVisible, on: accessOn },
		});
	}

	return cards;
}

// ── Onboarding visibility (never show what isn't available to the viewer) ──────
// The onboarding pick-steps must only ever SHOW — and pre-select, count, and
// install — capabilities the viewer can ACTUALLY add. The server's per-user
// `installable` (active + visibility + grants, migration 005) is the single source
// of truth. StepTools already routes its grid through buildCards (which drops
// isLocked entries), but StepSkills filtered on `kind` alone and the wizard's
// recommended pre-selection re-derived a stale `visibility==='admin'` rule —
// ignoring 'restricted', active=false, and `installable`. The result: a member saw
// admin-only / globally-deactivated SKILLS in onboarding, could select them, and hit
// a 403 at "Start chatting". These two helpers close that gap.
//
// Note we gate purely on `installable` (NOT isLocked, which keeps installed-but-
// unavailable cards visible to render an honest "Turned off by your admin" state):
// onboarding is a PICK surface with no such honest-state UI, so an item the viewer
// can't add should simply not appear.

// Entries of one kind the viewer can install right now. Skills don't twin-merge, so
// StepSkills can't reuse buildCards' Card shape — this is its equivalent gate.
export function onboardingEntries(entries: CatalogEntry[], kind: CapabilityKind): CatalogEntry[] {
	return entries.filter((e) => e.kind === kind && e.installable);
}

// Ids to pre-check in onboarding: recommended AND installable for THIS viewer. Never
// pre-select (and so never count or try to install) something the user can't add.
export function recommendedPickIds(entries: CatalogEntry[], kind: CapabilityKind): string[] {
	return entries.filter((e) => e.kind === kind && e.recommended && e.installable).map((e) => e.id);
}

// ── Honest status (shared by CapabilityCard render + the page tab pip/counts) ──
// HONEST HEALTH: 'ok' (green/confirmed) | 'warn' (needs the user / down) | 'none'
// (say nothing rather than claim a state we can't confirm). The page now renders
// 'ok' as SILENCE (no dot) — a healthy tool needs no indicator — and only paints
// the 'warn' state. But this helper keeps the THREE-way distinction: the page's
// "needs attention" pip + any future count rely on telling ok apart from none, so
// never collapse ok→none here.
//
// `adminOff` is checked BEFORE `!on`: an admin turned the cap off / revoked access
// while the user still has it installed (migration 005). The card stays visible
// with an honest "Turned off by your admin" warn — even though it reads as not-on.
export type CardStatusKind = 'ok' | 'warn' | 'none';
export type CardStatus = { kind: CardStatusKind; label: string };
export function cardStatus(card: Card): CardStatus {
	const on = card.installed && card.enabled;
	const requiresConn = card.requiresConnection;
	if (card.unavailableReason === 'admin_off') return { kind: 'warn', label: 'Turned off by your admin' };
	if (!on) return { kind: 'none', label: '' };
	// Skills are local files with no external service — when on, they're always
	// available. "Ready" here is the truth, not an unverifiable claim.
	if (card.kind === 'skill') return { kind: 'ok', label: 'Ready' };
	if (requiresConn) {
		// PREFER the broker-verified authState when present — it's the REAL auth truth,
		// where the group-aware `connected` boolean is a stale view that can read true
		// for a Google cap whose own grant is dead (the 401 it can't see). 'needs_auth'
		// gets its own honest "Sign-in expired" copy (distinct from a never-connected
		// cap's "Needs your key"); 'not_connected' reuses the needs-key warn.
		if (card.authState === 'not_connected') return { kind: 'warn', label: 'Needs your key' };
		if (card.authState === 'needs_auth') return { kind: 'warn', label: 'Sign-in expired' };
		// authState 'authed' (or undefined → fall through to the connected-based path).
		if (card.authState === undefined && !card.connected) return { kind: 'warn', label: 'Needs your key' };
		if (card.health === 'connected') return { kind: 'ok', label: 'Connected' };
		if (card.health === 'failed') return { kind: 'warn', label: 'Temporarily unavailable' };
		if (card.health === 'needs_auth') return { kind: 'warn', label: 'Needs your key' };
		return { kind: 'none', label: '' }; // unknown → don't claim "Connected"
	}
	if (card.health === 'connected') return { kind: 'ok', label: 'Ready' };
	if (card.health === 'failed') return { kind: 'warn', label: 'Temporarily unavailable' };
	if (card.health === 'needs_auth') return { kind: 'warn', label: 'Unavailable' };
	return { kind: 'none', label: '' }; // unknown / not yet probed → no claim
}

// ── Connector summary (home page "connectors enabled & live" indicator) ────────
// MCP-only rollup over merged Cards. "Connectors" are tools (kind 'mcp'); skills
// are excluded (a skill is always cardStatus 'ok', which would inflate "live").
// Cards are already twin-merged by buildCards, so this counts BRANDS — Asana's
// read/write pair is one connector.
//
// HONEST by construction (never-lie-in-ui): `live` reuses the authoritative
// cardStatus() rather than re-reading `health`, so an admin-off, needs-key,
// down, or not-yet-probed connector is correctly NOT counted live. `enabled` is
// what the user turned on (installed + enabled), minus admin-off caps the user
// can no longer actually use — so the page can honestly say "{live} of {enabled}
// live" when a probe hasn't confirmed one yet.
export function connectorSummary(cards: Card[]): { enabled: number; live: number } {
	let enabled = 0;
	let live = 0;
	for (const c of cards) {
		if (c.kind !== 'mcp') continue;
		if (c.installed && c.enabled && c.unavailableReason !== 'admin_off') enabled++;
		if (cardStatus(c).kind === 'ok') live++;
	}
	return { enabled, live };
}

// ── Skill grouping (Skills-room collapsible group blocks, migration 014) ───────
// Skills carry an optional `group` bucket (e.g. 'otto'). The Skills room renders
// ungrouped skills flat (as today), then one collapsible block per named group.
// These helpers are the pure, testable core: the partition + the group switch's
// tri-state. The action loop (install/setEnabled per member) stays in the page —
// it calls the store — but it derives its member lists straight off these Cards.

// Capitalize a group key for display ('otto' → 'Otto', 'amazon-ads' → 'Amazon Ads').
// Kept generic so any future group works without a per-id lookup table.
export function groupLabel(key: string): string {
	return key
		.split("-")
		.map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
		.join(" ");
}

export type SkillGroup = { key: string; label: string; cards: Card[] };

// Partition already-sorted, search-filtered skill Cards into a flat (ungrouped)
// list + ordered named groups. Order is preserved within each bucket, and group
// order follows first appearance. The page passes the SAME post-search, name-
// sorted list it renders, so filtered-out members are already gone (and so won't
// be bulk-toggled).
export function groupSkillCards(cards: Card[]): { ungrouped: Card[]; groups: SkillGroup[] } {
	const ungrouped: Card[] = [];
	const byKey = new Map<string, Card[]>();
	const order: string[] = [];
	for (const c of cards) {
		if (!c.group) {
			ungrouped.push(c);
			continue;
		}
		let arr = byKey.get(c.group);
		if (!arr) {
			arr = [];
			byKey.set(c.group, arr);
			order.push(c.group);
		}
		arr.push(c);
	}
	const groups = order.map((key) => {
		const grouped = byKey.get(key)!;
		// AMC Admin is supplemental inventory inspection, not the preferred access
		// twin. Keep scoped/default Analytics visually first even though DB name order
		// puts “Admin” first for admins.
		const ordered =
			key === 'amazon-amc'
				? [...grouped].sort((left, right) => {
						if (left.key === 'amazon-amc-readonly') return -1;
						if (right.key === 'amazon-amc-readonly') return 1;
						return 0;
					})
				: grouped;
		return { key, label: groupLabel(key), cards: ordered };
	});
	return { ungrouped, groups };
}

// The group switch's derived tri-state + the counts the header renders. Reuses
// the per-card semantics: a member is ON when installed && enabled && !admin_off;
// an admin_off member is LOCKED — it's excluded from the toggleable denominator,
// so it can't be flipped by enable-all/disable-all and never blocks the on-state
// (a fully-on group with one globally-disabled member still reads `on`).
export type GroupAggregate = {
	on: boolean; // every TOGGLEABLE member is on
	off: boolean; // no toggleable member is on
	indeterminate: boolean; // mixed
	enabledCount: number; // on (toggleable) members
	total: number; // all members
	lockedCount: number; // admin_off members (can't be toggled)
};
export function groupAggregate(cards: Card[]): GroupAggregate {
	const toggleable = cards.filter((c) => c.unavailableReason !== 'admin_off');
	const enabledCount = toggleable.filter((c) => c.installed && c.enabled).length;
	const total = cards.length;
	const lockedCount = total - toggleable.length;
	const on = toggleable.length > 0 && enabledCount === toggleable.length;
	const off = enabledCount === 0;
	return { on, off, indeterminate: !on && !off, enabledCount, total, lockedCount };
}

// ── In-chat connect card (the <sayano:connect> marker) ────────────────────────
// ConnectCapabilityCard renders the ONE next action a capability needs before the
// agent can use it. The marker carries only a capabilityId; the card resolves the
// raw CatalogEntry (NOT a merged Card — the agent names a concrete id, and the
// connect/enable/install actions all act on that exact id) and derives a single
// step. Pure + exported so the derivation is unit-testable without the runtime.
//
// Order mirrors the marketplace card's own precedence (CapabilityCard.power):
//   admin_off → blocked; not installed → add; disabled → enable; needs a key/login
//   → connect (oauth vs vault_bearer); otherwise → ready.
//
// MARKER-AS-RUNTIME-SIGNAL: a <sayano:connect> marker that actually reaches the
// card is live ground-truth that the agent couldn't use the cap THIS turn (the
// agent emits it only for an unusable cap, and the agent-side guard keeps it only
// for an id in the user's visible catalog). The catalog's group-aware `connected`
// (PR #129) and any healthUrl probe are a STALE view by comparison — both can read
// 'connected' for a grouped Google cap whose per-request OAuth token is actually
// expired (the 401 the probe never sees). So when the caller is the in-chat card
// (`agentRequested`), a surviving marker for a requiresConnection cap that ALSO
// reads connected means "reconnect", not the false-green "ready".
export type ConnectStep =
	| { kind: 'unavailable' } // admin turned it off / revoked — not the user's to fix here
	| { kind: 'operator_setup' } // shared company connector is unconfigured/down — no user credential can fix it
	| { kind: 'install' } // not installed → POST /installs
	| { kind: 'enable' } // installed but disabled → PATCH /installs/:id { enabled:true }
	| { kind: 'connect_oauth' } // needs a provider sign-in → POST /connections → authorizeUrl
	| { kind: 'connect_token' } // needs a pasted key → POST /connections { token }
	| { kind: 'reconnect' } // installed+enabled+connected, BUT the agent emitted a <sayano:connect> marker for it this turn → its credential is actually broken (e.g. a 401 the group-aware `connected` can't see). Re-run the OAuth/token flow.
	| { kind: 'ready' }; // installed + enabled + (connected OR no connection needed)

export const OPERATOR_SETUP_MESSAGE =
	'This company connector is unavailable. An administrator needs to finish its setup or restore the service.';

// `agentRequested` defaults false so every /marketplace CapabilityCard caller (which
// passes a merged-card-derived entry, never a marker) keeps today's behavior. The
// in-chat ConnectCapabilityCard passes true: it ONLY ever renders from a live marker.
export function connectStep(entry: CatalogEntry, agentRequested = false): ConnectStep {
	if (entry.unavailableReason === 'admin_off') return { kind: 'unavailable' };
	if (!entry.installed) return { kind: 'install' };
	if (!entry.enabled) return { kind: 'enable' };
	if (
		entry.kind === 'mcp' &&
		entry.auth === 'shared_bearer' &&
		entry.health !== undefined &&
		entry.health !== 'connected'
	) {
		return { kind: 'operator_setup' };
	}

	// requiresConnection caps need a live credential before they're usable. (Skills
	// and auth 'none'/'shared_bearer' MCPs never set requiresConnection.) PREFER the
	// broker-verified `authState` when the manager supplies it — it's the REAL auth
	// truth, where the group-aware `connected` boolean is a stale view that can read
	// true for a Google cap whose own grant is dead (the 401 it can't see).
	if (entry.requiresConnection && entry.authState !== undefined) {
		switch (entry.authState) {
			case 'not_connected':
				return entry.auth === 'oauth' ? { kind: 'connect_oauth' } : { kind: 'connect_token' };
			case 'needs_auth':
				// The grant exists but is dead — reconnect even WITHOUT a marker. This is
				// the authoritative dead-grant signal (broker-proven), so it doesn't need
				// the agent to have hit a 401 this turn.
				return { kind: 'reconnect' };
			case 'authed':
				// Real auth is fine. A surviving <sayano:connect> marker (agentRequested)
				// still wins for the residual mid-turn case (auth went stale AFTER the
				// broker check, mid-turn) — offer Reconnect rather than a false Ready.
				return agentRequested ? { kind: 'reconnect' } : { kind: 'ready' };
		}
	}

	// authState absent (older payload / non-requiresConnection cap) → the pre-authState
	// behavior verbatim: connect_* when not connected, then the marker-only reconnect
	// fallback for a connected cap the agent still couldn't use this turn.
	if (entry.requiresConnection && !entry.connected) {
		return entry.auth === 'oauth' ? { kind: 'connect_oauth' } : { kind: 'connect_token' };
	}
	// A surviving <sayano:connect> marker (agentRequested) for a connected cap means
	// the agent hit a real auth failure the catalog's group-aware `connected` can't
	// see (the mid-turn 401). The marker is authoritative; offer Reconnect instead of
	// a false-green Ready. (A merely-stale probe with a WORKING token can't trigger
	// this: the agent's tool call would have succeeded → no marker → this card never
	// renders, so we never consult the spurious health='failed'.)
	if (agentRequested && entry.requiresConnection) return { kind: 'reconnect' };
	return { kind: 'ready' };
}

// What the connect card may RENDER, given the step and whether a mutation for this
// capability is currently in flight (store `busy`). The store patches the entry
// optimistically the instant Enable/Add is clicked, so connectStep flips to 'ready'
// seconds BEFORE the manager's response — which is only sent after the server-side
// loadout rebuild lands on disk. Surfacing Ready/Continue in that window lets the
// user hand "please continue" to the agent against the PRE-rebuild loadout (session
// 1630214d: Enable → Continue 0.9s apart; the retry turn read the loadout 3.5s
// before zendesk-readonly appeared in it). So: while busy, 'ready' renders as
// 'settling' (a spinner row); every other step passes through so the in-flight
// button keeps its own busy spinner.
export type DisplayStep = ConnectStep | { kind: 'settling' };
export function displayStep(step: ConnectStep, busy: boolean): DisplayStep {
	if (busy && step.kind === 'ready') return { kind: 'settling' };
	return step;
}
